import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "../../../components/ui/Button";
import { useI18n } from "../../../i18n";
import { isBenchUrl, returnUrlHere } from "../../../lib/bench/handoff";
import type { BenchSource, FieldApi } from "../../../lib/api/fieldApi";
import { SIM_FRAME_ALLOW, SIM_FRAME_SANDBOX, simViewUrl } from "../lib/simView";

/**
 * 놀이터 왼쪽 칸 — 내 micro:bit 작품을 MakeCode 시뮬레이터로 돌린다(Bench 편집기 '시뮬레이터만 보기' · 1회용 입장표 · iframe).
 * 시뮬레이터의 화면 · 입력 · 라디오는 이 칸(Bench)이 부모 창(여기)으로 보낸다 — 놀이터(오른쪽)는 그 소식으로 움직인다.
 *  - 작품이 하나(또는 지금 차시의 작품)면 처음에 저절로 불러온다 · 여럿이면 고른다.
 *  - [다시 불러오기] — 'Bench 에서 고치기' 탭에서 고친 것을 가져온다(새 입장표 · 칸은 저장하지 않는 재생 전용이라 덮어쓸 걱정이 없다).
 *  - `onWindow` — 칸의 창(소식을 이 창에서 온 것만 받게 · 라디오를 이 창으로 보내게).
 *  - `mirror` — 진짜 보드가 연결되면 시뮬레이터를 내리고 그 자리에 보드 거울을 그린다(시뮬레이터는 따로 돌아 헷갈리고 라디오가 광장에 섞인다).
 *    연결을 끊으면 새 입장표로 다시 불러온다(입장표는 1회용).
 */
export function SimPane({ api, benchOrigin, projects, preferred, onWindow, onEdit, editBusy, mirror }: {
  api: FieldApi | null;
  benchOrigin: string | null;
  projects: BenchSource[] | null;
  preferred: string | null;
  onWindow: (w: Window | null) => void;
  onEdit: (projectId: string) => void;
  editBusy: boolean;
  mirror?: ReactNode;
}) {
  const { t } = useI18n();
  const [projectId, setProjectId] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const frame = useRef<HTMLIFrameElement | null>(null);
  const auto = useRef(false);

  const load = async (id: string) => {
    if (!api || !benchOrigin || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const { editorUrl } = await api.benchHandoff(id, returnUrlHere());
      if (!isBenchUrl(editorUrl, benchOrigin)) throw new Error("editorUrl outside bench origin");
      setProjectId(id);
      setUrl(simViewUrl(editorUrl));
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  // 고를 것이 분명하면 저절로 — 아이가 직접 고르기 전까지는 더 맞는 작품(preferred — 오늘의 미션 차시의 작품)이 늦게 알려져도 그것으로 바꾼다
  // (2026-10-06 운영 실측: 미션이 작품 목록보다 늦게 와서 다른 차시 작품이 먼저 실렸다). 아이가 고른 뒤에는 건드리지 않는다
  const autoId = useRef<string | null>(null);
  useEffect(() => {
    if (!projects || !api || !benchOrigin) return;
    // 처음부터 시뮬레이터를 보인다(2026-10-06 사용자 요청) — 고를 것이 분명하지 않으면 가장 최근에 고친 작품
    const pick = preferred ?? newestProject(projects);
    if (!pick) return;
    if (auto.current && (autoId.current === null || autoId.current === pick)) return;
    auto.current = true;
    autoId.current = pick;
    void load(pick);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 작품 목록 · 고를 작품이 바뀔 때만
  }, [projects, api, benchOrigin, preferred]);
  /** 아이가 직접 골랐다 — 이제 저절로 바꾸지 않는다 */
  const pickByHand = (id: string) => { auto.current = true; autoId.current = null; void load(id); };

  // 거울 ↔ 시뮬레이터 — 내릴 때 창을 비우고, 다시 올릴 때 새 입장표로
  const mirrored = mirror !== undefined && mirror !== null;
  const wasMirrored = useRef(mirrored);
  useEffect(() => {
    if (mirrored) onWindow(null);
    else if (wasMirrored.current && projectId) void load(projectId);
    wasMirrored.current = mirrored;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 거울이 바뀔 때만
  }, [mirrored]);

  const current = projects?.find((p) => p.projectId === projectId) ?? null;
  return (
    <div className="pf-mb__sim" data-testid="fs-mb-sim">
      {mirrored ? mirror : url ? (
        <iframe key={url} ref={(el) => { frame.current = el; if (el) onWindow(el.contentWindow); }} src={url} title={t("student.microbit.sim.title")} className="pf-mb__sim-frame"
                allow={SIM_FRAME_ALLOW} sandbox={SIM_FRAME_SANDBOX} allowFullScreen data-testid="fs-mb-sim-frame"
                onLoad={() => onWindow(frame.current?.contentWindow ?? null)} />
      ) : (
        <div className="pf-mb__sim-empty" data-testid="fs-mb-sim-empty">
          {busy ? t("student.microbit.sim.loading") : projects && projects.length === 0 ? t("student.microbit.none") : t("student.microbit.sim.pick")}
        </div>
      )}
      {failed ? <p className="pf-mb__hint" role="alert" data-testid="fs-mb-sim-failed">{t("student.microbit.sim.failed")}</p> : null}
      <div className="pf-mb__sim-actions">
        {current ? (
          <>
            <Button variant="ghost" icon="🔄" disabled={busy} onClick={() => void load(current.projectId)} data-testid="fs-mb-sim-reload">
              {t("student.microbit.sim.reload")}
            </Button>
            <Button variant="ghost" icon="✏️" disabled={editBusy || !api || !benchOrigin} onClick={() => onEdit(current.projectId)} data-testid="fs-mb-open">
              {t("student.microbit.sim.edit")}
            </Button>
          </>
        ) : null}
        {projects && projects.length > 0 ? (
          <label className="pf-mb__sim-pick">
            <span>{t("student.microbit.sim.pickLabel")}</span>
            {/* 지금 돌아가는 작품이 골라져 있다 — 다른 작품을 고르면 바로 불러온다(2026-10-06 사용자 요청: 단추 여러 개 → 선택 목록) */}
            <select value={projectId ?? ""} disabled={busy || !api || !benchOrigin} onChange={(e) => { if (e.target.value) pickByHand(e.target.value); }}
                    data-testid="fs-mb-sim-pick" aria-label={t("student.microbit.sim.pickLabel")}>
              {projectId === null ? <option value="">{t("student.microbit.sim.pickNone")}</option> : null}
              {projects.map((p) => <option key={p.projectId} value={p.projectId}>{p.title}</option>)}
            </select>
          </label>
        ) : null}
      </div>
      {current ? <p className="pf-mb__hint">{t("student.microbit.sim.hint", { title: current.title })}</p> : null}
    </div>
  );
}

/** 가장 최근에 고친 작품(없으면 null) */
export function newestProject(projects: readonly BenchSource[]): string | null {
  let best: BenchSource | null = null;
  for (const p of projects) if (!best || p.updatedAt > best.updatedAt) best = p;
  return best?.projectId ?? null;
}
